import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment as env } from '../../../environments/environment';
import { AgGridAngular } from 'ag-grid-angular';
import type { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { agGridThemes } from '../../@shared/ag-grid-themes';

interface BreakoutRow {
  email: string;
  first_name: string;
  last_name: string;
  customer: string;
  scanner: string;
  start_date: string | null;
  end_date: string | null;
  source: string;
  pool_id: string;
  delta_type: 'active' | 'joined_this_month' | 'departed_this_month';
  // IT-F2-157 c/9a55ad31 (Mike 2026-10-01): populated for Logins-row
  // breakout rows (per-email AuthLogins count for the month). Column
  // hidden in the UI when every row has it undefined.
  login_count?: number;
  // IT-F2-157 2026-10-30: populated when the Billable toggle is on and
  // this row has a matching non-fallback CommerceEvents row (null in
  // non-billable rows, which are returned under non_billable_rows).
  // commerce_event_id added c/c343b2ac so the joined chip deep-links
  // to /admin/commerce-events/:id for the exact activation event.
  billable_match?: { at: string; commerce_event_id?: string } | null;
  // IT-F2-157 Mike c/followup (2026-10-30): billable user without a
  // CommerceEvents backing (admin-added, pre-TTL purchase, backfill).
  // Mike: "commerce events are newer technology so we need if there's
  // someone to bill for that doesn't have a commerce event just flag
  // them butt bill for them". Rendered as an amber warning badge.
  no_commerce_match?: boolean;
  // IT-F2-157 Mike c/b41aa4b6 (2026-10-30): client-stamped boolean so
  // the merged grid can split billable vs non-billable in one view.
  _billable?: boolean;
}

interface BreakoutResponse {
  customer: string;
  scanner: string;
  month: string;
  is_customer_row: boolean;
  count: number;
  joined_count: number;
  departed_count: number;
  rows: BreakoutRow[];
  err?: string;
  // IT-F2-157 c/9a55ad31: present only on Logins-row breakouts.
  total_logins?: number;
  // IT-F2-157 Mike c/7d710eb1 (2026-10-30): 'last_logins' = Redis-sourced
  // Last Logins row; relabel Start / End columns to "Last Login" / "Access
  // End" so admins don't misread the single-timestamp signal.
  source_kind?: 'logins' | 'last_logins';
  // IT-F2-157 2026-10-30: Billable-mode diagnostics.
  billable_only?: boolean;
  pre_ttl_window?: boolean;
  non_billable_count?: number;
  non_billable_rows?: BreakoutRow[];
}

// IT-F2-157 item 10e3cea4 — click any billing-report cell → this
// standalone detail page. URL is admin-gated (AdminAuthGuard on the
// route + F2AuthMiddleware role=admin on the backend). Backend user
// enrichment reads Redis USER_CACHE, never Cognito directly (fleet
// rule feedback_user_display_reads_cache_never_cognito).
@Component({
  selector: 'app-billing-breakout',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, AgGridAngular],
  template: `
    <div class="w-full h-full flex flex-col py-6 px-6 text-white">
      <div class="flex items-center gap-3 mb-4">
        <!--
          IT-F2-157 Mike c/edca24cd (2026-10-30): carry customer + the
          other filters forward on the back-nav so the parent report
          page re-opens with the same scope the user drilled from.
        -->
        <a [routerLink]="['/admin/billing-reports']" [queryParams]="back_query_params()" class="dark-btn">
          <i class="fa-solid fa-arrow-left mr-1"></i> Back to Billing Report
        </a>
        <h1 class="text-lg font-semibold ml-2">
          <!-- IT-F2-157 Mike c/97f291da (2026-10-30): header reflects
               the range when ?months=M1,...,M6 is in the URL. -->
          Billing Breakout · {{ customer }} / {{ scanner }} /
          <span *ngIf="months.length">{{ month_label(months[0]) }} – {{ month_label(months[months.length - 1]) }} ({{ months.length }}-month total)</span>
          <span *ngIf="!months.length">{{ month_label(month) }}</span>
        </h1>
      </div>

      <ng-container *ngIf="loading">
        <p class="text-white/60">Loading…</p>
      </ng-container>

      <ng-container *ngIf="error">
        <p class="text-red-400">Error: {{ error }}</p>
      </ng-container>

      <ng-container *ngIf="data && !loading && !error">
        <div class="flex items-center gap-4 mb-3 text-sm">
          <span class="px-3 py-1 rounded bg-white/5 border border-white/10"
                [title]="data.billable_only ? 'Billable-filtered count — non-fallback CommerceEvents match required at or before end-of-month' : 'Legacy entitlement count'">
            <strong>{{ data.count }}</strong> users
            <span *ngIf="data.billable_only" class="ml-1 text-emerald-400 font-mono">($)</span>
          </span>
          <!-- IT-F2-157 2026-10-30 — Non-billable tray header. Rows
               appear in a secondary table below the primary tbody so
               Mike can see exactly who's being filtered out and will
               later target with the Retro-revoke action (Phase 2, dry-
               run first). -->
          <span *ngIf="data.billable_only && (data.non_billable_count || 0) > 0"
                class="px-3 py-1 rounded bg-amber-950/40 border border-amber-700/40 text-amber-300"
                title="Users whose UserTracking grant has NO matching non-fallback CommerceEvents row — likely SamCart default_scanner fallback grants. Retro-revoke candidates.">
            {{ data.non_billable_count }} non-billable
          </span>
          <!-- IT-F2-157 Mike c/ef267645 (2026-10-30): preview button
               now visible whenever Billable mode is on, regardless of
               the current month's non-billable count. The preview
               endpoint pulls the full-ever fallback cohort for this
               scanner (not scoped to the breakout's month), so a cell
               with 0 non-billable in this month can still have real
               fallback-mistake users elsewhere in the cohort. Modal
               handles the empty-set case gracefully. -->
          <button *ngIf="data.billable_only"
                  type="button" class="dark-btn text-xs"
                  title="Preview the retro-revoke plan for this scanner's full fallback-only cohort (read-only; no writes)"
                  (click)="open_retro_revoke_preview()">
            <i class="fa-solid fa-fw fa-magnifying-glass mr-1 dark:text-amber-300"></i> Preview retro-revoke
          </button>
          <span *ngIf="data.billable_only && data.pre_ttl_window"
                class="px-3 py-1 rounded bg-white/5 border border-white/15 text-white/60"
                title="This month ends before the CommerceEvents 365d TTL — Billable count cannot be fully verified; some real purchases may have aged out of the collection.">
            pre-TTL window
          </span>
          <span *ngIf="data.source_kind === 'logins' && data.total_logins !== undefined" class="px-3 py-1 rounded bg-sky-950/40 border border-sky-700/40 text-sky-300">
            {{ data.total_logins }} logins total
          </span>
          <span *ngIf="data.joined_count > 0" class="px-3 py-1 rounded bg-emerald-950/40 border border-emerald-700/40 text-emerald-300">
            +{{ data.joined_count }} joined
          </span>
          <span *ngIf="data.departed_count > 0" class="px-3 py-1 rounded bg-red-950/40 border border-red-700/40 text-red-300">
            −{{ data.departed_count }} departed
          </span>
          <div class="ml-auto flex items-center gap-3">
            <!-- IT-F2-157 c/21eaaf56 (Mike 2026-10-01): checkbox for
                 scoping Copy Debug payload. When unchecked, the
                 snapshot includes query + counts only (no rows). -->
            <label class="flex items-center gap-1.5 text-xs text-white/70 cursor-pointer select-none"
                   title="When checked, the Copy Debug clipboard payload includes the backend response rows (filtered to selected users if any are picked). Uncheck for a lean query+counts snapshot.">
              <input type="checkbox" class="w-4 h-4 dark-checkbox" [(ngModel)]="debug_include_data" />
              Include data
            </label>
            <button type="button" class="dark-btn"
                    [title]="debug_include_data
                      ? (selected.size > 0
                          ? ('Copy JSON snapshot with ' + selected.size + ' selected row(s)')
                          : ('Copy JSON snapshot with all ' + (data.rows.length || 0) + ' row(s)'))
                      : 'Copy JSON snapshot with query + counts only (no rows)'"
                    (click)="copy_debug()">
              <i class="fa-solid fa-fw mr-1" [ngClass]="debug_state === 'ok' ? 'fa-check text-emerald-400' : (debug_state === 'err' ? 'fa-xmark text-red-400' : 'fa-clipboard text-sky-400')"></i>
              <!-- IT-F2-157 Mike c/26f82aeb (2026-10-30): button label
                   carries the row count the debug payload will include
                   so admins can see at a glance what's going to land on
                   the clipboard. Only shows a count when include_data
                   is on; otherwise it's a lean counts-only snapshot. -->
              {{ debug_state === 'ok' ? 'Copied' : (debug_state === 'err' ? 'Copy failed' : (debug_include_data ? ('Copy Debug (' + copy_count() + ')') : 'Copy Debug')) }}
            </button>
            <button type="button" class="dark-btn" (click)="copy_to_excel()" [disabled]="copying"
                    [title]="selected.size > 0 ? 'Copy ' + selected.size + ' selected row(s)' : 'Copy all ' + (data.rows.length || 0) + ' row(s)'">
              <i class="fa-solid fa-fw mr-1" [ngClass]="copy_state === 'ok' ? 'fa-check text-emerald-400' : (copy_state === 'err' ? 'fa-xmark text-red-400' : 'fa-copy text-blue-400')"></i>
              <!-- IT-F2-157 Mike c/7d710eb1 (2026-10-30): button label
                   carries the row count that will actually be copied so
                   admins can see at a glance how many rows the clipboard
                   will contain (selected size when any are picked, else
                   the full row count). -->
              {{ copy_state === 'ok' ? ('Copied ' + copy_count()) : (copy_state === 'err' ? 'Copy failed — Ctrl+C' : ('Copy to Excel (' + copy_count() + ')')) }}
            </button>
          </div>
        </div>
        <!-- IT-F2-157 c/21eaaf56 (Mike 2026-10-01): selection count hint so
             operators know how Copy Debug + Copy to Excel will scope. -->
        <div class="text-xs text-white/50 mb-2" *ngIf="selected.size > 0">
          {{ selected.size }} of {{ data.rows.length }} selected — Copy actions will export only the picked rows
        </div>

        <textarea #tsvArea class="sr-only" tabindex="-1" aria-hidden="true">{{ tsv }}</textarea>

        <!--
          IT-F2-157 Mike c/4c764a18 (2026-10-30): ag-grid replaces the
          plain HTML table. Per-column text filters via floatingFilter,
          sticky headers, pinned Delta column, multi-row selection with
          header checkbox; the grid stretches to the viewport (flex-1 +
          min-h-0 is the critical flexbox incantation for nested
          overflow chains).
        -->
        <!--
          IT-F2-157 Mike c/b41aa4b6 (2026-10-30): the Non-billable tray
          lived below the primary table and read as a second grid.
          Merged into one ag-grid feed via all_grid_rows() — each row
          carries a _billable boolean so the admin can filter via the
          Billable column (set filter) to see just billable, just non-
          billable, or both. Row background still tints by delta_type
          OR by _billable=false (amber) so the two populations stay
          visually distinct inside the single grid.
        -->
        <!--
          IT-F2-157 Mike c/813b42ee (2026-10-30): fleet dark theme via
          agGridThemes.dark (themeBalham + colorSchemeDark) matches the
          Billing / Billing Reports / Users panels. The previous
          ag-theme-alpine-dark class name is a leftover from the
          pre-theming-API path and doesn't apply the dark palette on
          current ag-grid versions.
        -->
        <div class="w-full flex-1 min-h-0"
             style="height: calc(100vh - 320px);">
          <ag-grid-angular
            class="h-full w-full"
            [theme]="gridTheme"
            [rowData]="all_grid_rows(data)"
            [columnDefs]="columnDefs(data)"
            [gridOptions]="gridOptions"
            (gridReady)="on_grid_ready($event)"
            (selectionChanged)="on_selection_changed()"
          ></ag-grid-angular>
        </div>

        <!-- IT-F2-157 2026-10-30 — Non-billable tray.
             Rows for T/C-source users whose UserTracking grant has no
             matching non-fallback CommerceEvents row at or before
             end-of-month. These are the retro-revoke candidates per
             Mike c/37bcd0e4. The Phase-2 Retro-revoke button (dry-run
             first) will target this list. For now this is read-only
             and informational. -->
      </ng-container>

      <!-- IT-F2-157 Mike c/144ca889 + operator green-light (2026-10-30):
           Retro-revoke preview modal. Read-only — displays the preview
           endpoint payload (fallback-only cohort + planned mutations)
           so Mike can validate the specific users before any write-
           enabled Phase 2 ships. Export CSV lets him audit the list
           outside the SPA. Writes stay blocked. -->
      <div *ngIf="retro_preview_open"
           class="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
           (click)="retro_preview_open = false">
        <div class="bg-app-black-100 border border-white/15 rounded-lg shadow-xl max-w-5xl w-full max-h-[85vh] flex flex-col"
             (click)="$event.stopPropagation()">
          <div class="px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <h3 class="text-base font-semibold text-amber-300">
              <i class="fa-solid fa-triangle-exclamation mr-1"></i>
              Retro-revoke preview — {{ scanner }} (read-only)
            </h3>
            <button class="text-white/50 hover:text-white" (click)="retro_preview_open = false">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
          <div class="px-5 py-3 flex-1 overflow-auto text-sm text-white">
            <p *ngIf="retro_preview_loading" class="text-white/60">Loading preview…</p>
            <p *ngIf="retro_preview_err" class="text-red-400">Error: {{ retro_preview_err }}</p>
            <ng-container *ngIf="retro_preview && !retro_preview_loading && !retro_preview_err">
              <div class="flex items-center gap-3 mb-3 text-xs">
                <span class="px-2 py-1 rounded bg-white/5 border border-white/10">
                  <strong>{{ retro_preview.summary?.total || 0 }}</strong> users in revoke set
                </span>
                <span *ngIf="retro_preview.filters?.scanner" class="px-2 py-1 rounded bg-white/5 border border-white/10 font-mono">
                  scanner: {{ retro_preview.filters.scanner }}
                </span>
                <span *ngIf="retro_preview.filters?.customer" class="px-2 py-1 rounded bg-white/5 border border-white/10 font-mono">
                  customer: {{ retro_preview.filters.customer }}
                </span>
                <span class="px-2 py-1 rounded bg-amber-950/40 border border-amber-700/40 text-amber-300">
                  DRY-RUN — no writes
                </span>
              </div>
              <div class="text-xs text-white/60 mb-3">
                Per-scanner: <code class="text-white/80 font-mono">{{ retro_preview.summary?.by_scanner | json }}</code>
                &nbsp;·&nbsp; Per-customer: <code class="text-white/80 font-mono">{{ retro_preview.summary?.by_customer | json }}</code>
              </div>
              <div class="w-full overflow-x-auto">
                <table class="w-full text-xs">
                  <thead>
                    <tr class="text-left border-b border-white/15">
                      <th class="py-2 px-3">Email</th>
                      <th class="py-2 px-3">Customer</th>
                      <th class="py-2 px-3">Scanner</th>
                      <th class="py-2 px-3">Pool</th>
                      <th class="py-2 px-3">Enabled</th>
                      <th class="py-2 px-3">Current scanners</th>
                      <th class="py-2 px-3">Cognito after</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let p of retro_preview.plans" class="border-b border-white/5 hover:bg-white/5">
                      <td class="py-1.5 px-3 font-mono">{{ p.email }}</td>
                      <td class="py-1.5 px-3">{{ p.customer || '-' }}</td>
                      <td class="py-1.5 px-3 font-mono text-amber-300">{{ p.scanner }}</td>
                      <td class="py-1.5 px-3 font-mono text-white/60">{{ p.pool_id || '-' }}</td>
                      <td class="py-1.5 px-3" [ngClass]="p.enabled === true ? 'text-emerald-400' : (p.enabled === false ? 'text-red-400' : 'text-white/40')">
                        {{ p.enabled === null ? 'not found' : (p.enabled ? 'yes' : 'no') }}
                      </td>
                      <td class="py-1.5 px-3 font-mono text-xs text-white/70">{{ (p.current_scanners || []).join(',') }}</td>
                      <td class="py-1.5 px-3 font-mono text-xs text-sky-300">{{ p.plan?.cognito?.after || '-' }}</td>
                    </tr>
                    <tr *ngIf="(retro_preview.plans || []).length === 0">
                      <td colspan="7" class="py-4 px-3 text-center text-white/50">No users in the revoke set for this filter.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </ng-container>
          </div>
          <div class="px-5 py-3 border-t border-white/10 flex items-center gap-3">
            <button type="button" class="dark-btn text-xs"
                    (click)="copy_retro_preview()"
                    [disabled]="!retro_preview || retro_preview_loading">
              <i class="fa-solid fa-fw fa-clipboard mr-1 dark:text-sky-400"></i>
              Copy preview JSON
            </button>
            <button type="button" class="dark-btn text-xs"
                    (click)="export_retro_preview_csv()"
                    [disabled]="!retro_preview || retro_preview_loading">
              <i class="fa-solid fa-fw fa-file-csv mr-1 dark:text-emerald-400"></i>
              Export CSV
            </button>
            <span class="text-xs text-white/50 ml-auto">
              Write-enabled Phase 2 blocked pending operator sign-off after review.
            </span>
            <button type="button" class="dark-btn text-xs" (click)="retro_preview_open = false">Close</button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; height: 100%; }
    .sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); border: 0; }
  `],
})
export class BillingBreakoutComponent implements OnInit {
  customer = '';
  scanner = '';
  month = '';
  loading = true;
  error = '';
  data: BreakoutResponse | null = null;
  tsv = '';
  copying = false;
  copy_state: 'idle' | 'ok' | 'err' = 'idle';
  // IT-F2-157 c/9a55ad31 (Mike 2026-10-01): parity with the
  // billing-reports Copy Debug button.
  debug_state: 'idle' | 'ok' | 'err' = 'idle';
  // IT-F2-157 c/21eaaf56 (Mike 2026-10-01): row-select checkboxes +
  // "include raw data" toggle for Copy Debug so operators can scope
  // the clipboard payload to the users they care about.
  selected: Set<string> = new Set<string>();
  debug_include_data = true;

  constructor(private route: ActivatedRoute, private http: HttpClient) {}

  // IT-F2-157 2026-10-30 — carry the Billable toggle through from the
  // billing-report grid so a drill-down shows the filtered row set + the
  // non-billable tray matching that count.
  billable_only = false;

  // IT-F2-157 Mike c/4c764a18 (2026-10-30) — ag-grid state.
  gridApi: GridApi | null = null;
  // IT-F2-157 Mike c/813b42ee (2026-10-30) — bind the fleet dark theme
  // via the theming API (same reference the Billing / Users panels use).
  gridTheme = agGridThemes.dark;
  gridOptions: any = {
    rowSelection: {
      mode: 'multiRow',
      checkboxes: true,
      headerCheckbox: true,
      selectAll: 'filtered',
      enableClickSelection: false,
    },
    defaultColDef: {
      resizable: true,
      sortable: true,
      filter: true,
      floatingFilter: true,
      cellStyle: { 'text-align': 'left', 'display': 'flex', 'align-items': 'center' },
    },
    getRowId: (params: any) => `${params.data?.email || ''}::${params.data?.scanner || ''}`,
    animateRows: false,
    suppressRowClickSelection: true,
    rowHeight: 30,
    headerHeight: 32,
    floatingFiltersHeight: 32,
    getRowStyle: (params: any) => {
      // IT-F2-157 Mike c/b41aa4b6 (2026-10-30): non-billable rows get
      // an amber tint matching the summary chip at the top; delta_type
      // tints only apply to billable rows.
      if (params?.data?._billable === false) return { 'background-color': 'rgba(245,158,11,0.08)' };
      const dt = params?.data?.delta_type;
      if (dt === 'joined_this_month') return { 'background-color': 'rgba(16,185,129,0.08)' };
      if (dt === 'departed_this_month') return { 'background-color': 'rgba(239,68,68,0.08)' };
      return undefined;
    },
  };

  // IT-F2-157 Mike c/b41aa4b6 (2026-10-30) — fold the primary + non-
  // billable rows into one array for the grid, stamping each row with
  // _billable so the Billable column (set filter) can split the two.
  all_grid_rows(data: BreakoutResponse): BreakoutRow[] {
    const billable = (data.rows || []).map(r => ({ ...r, _billable: true }));
    const non = (data.non_billable_rows || []).map(r => ({ ...r, _billable: false }));
    return [...billable, ...non];
  }

  // IT-F2-157 Mike c/4c764a18 (2026-10-30) — column defs, driven by the
  // response's source_kind so Start/End render as datetime (logins /
  // last_logins) or calendar date (UserTracking paths), and the Logins
  // count column only appears for logins-source breakouts. The Delta
  // column carries the joined → CommerceEvents deep-link via a raw-HTML
  // cell renderer (Mike c/c343b2ac).
  columnDefs(data: BreakoutResponse): ColDef[] {
    const sk = data.source_kind;
    const startDatetime = sk === 'logins' || sk === 'last_logins';
    const endDatetime = sk === 'logins';
    const defs: ColDef[] = [
      // IT-F2-157 Mike c/b41aa4b6 (2026-10-30): Billable column
      // (set filter, pinned left) so admins can split the two
      // populations in-place without a second grid below.
      ...(data.billable_only ? [{
        field: '_billable',
        headerName: 'Billable',
        minWidth: 150,
        pinned: 'left' as const,
        filter: 'agSetColumnFilter',
        valueFormatter: (p: any) => p.value === false
          ? 'non-billable'
          : (p.data?.no_commerce_match ? 'billable (no CE match)' : 'billable'),
        // IT-F2-157 Mike c/followup (2026-10-30): billable rows WITHOUT
        // a CommerceEvents backing get a warning badge so admins can
        // see the gap — these users are billable per Mike's
        // "entitlement = authority" rule but lack commerce
        // attribution (admin-added, pre-TTL, backfill).
        cellRenderer: (p: any) => {
          if (p.data?._billable === false) return '<span class="text-amber-300">⚠ non-bill</span>';
          if (p.data?.no_commerce_match) return '<span class="text-sky-300" title="Billable per entitlement but no CommerceEvents row (admin-added, pre-TTL purchase, or backfill)">$ bill ⚠ no-CE</span>';
          return '<span class="text-emerald-300">$ billable</span>';
        },
      }] : []),
      { field: 'email', headerName: 'Email', minWidth: 220, cellClass: 'font-mono text-xs' },
      { field: 'first_name', headerName: 'First', minWidth: 100 },
      { field: 'last_name', headerName: 'Last', minWidth: 110 },
      { field: 'customer', headerName: 'Customer', minWidth: 120 },
      { field: 'scanner', headerName: 'Scanner', minWidth: 140 },
      {
        field: 'start_date',
        headerName: sk === 'logins' ? 'First Login' : sk === 'last_logins' ? 'Last Login' : 'Start',
        minWidth: 160,
        valueFormatter: (p: any) => startDatetime ? this.format_datetime(p.value) : this.format_date(p.value),
      },
      {
        field: 'end_date',
        headerName: sk === 'logins' ? 'Last Login' : sk === 'last_logins' ? 'Access End' : 'End',
        minWidth: 140,
        valueFormatter: (p: any) => endDatetime ? this.format_datetime(p.value) : this.format_date(p.value),
      },
      { field: 'source', headerName: 'Src', minWidth: 70, cellClass: 'font-mono text-emerald-400' },
      { field: 'pool_id', headerName: 'Pool', minWidth: 140, cellClass: 'text-xs text-white/60' },
    ];
    if (sk === 'logins') {
      defs.push({
        field: 'login_count',
        headerName: 'Logins',
        minWidth: 90,
        cellClass: 'font-mono text-sky-300 text-right',
      });
    }
    defs.push({
      field: 'delta_type',
      headerName: 'Delta',
      minWidth: 130,
      pinned: 'right',
      cellRenderer: (params: any) => {
        const row = params?.data;
        if (!row) return '';
        if (row.delta_type === 'joined_this_month') {
          const id = row.billable_match?.commerce_event_id;
          if (id) {
            const safe = String(id).replace(/[^a-zA-Z0-9]/g, '');
            return `<a href="/admin/commerce-events/${encodeURIComponent(id)}" target="_blank" rel="noopener" class="text-emerald-300 underline decoration-dotted decoration-emerald-300/40 underline-offset-2 hover:decoration-emerald-300" title="View CommerceEvents row ${safe}">+ joined</a>`;
          }
          return '<span class="text-emerald-300">+ joined</span>';
        }
        if (row.delta_type === 'departed_this_month') return '<span class="text-red-300">− departed</span>';
        return '<span class="text-white/50">active</span>';
      },
    });
    return defs;
  }

  on_grid_ready(ev: GridReadyEvent) {
    this.gridApi = ev.api;
    try { ev.api.sizeColumnsToFit(); } catch { /* ignore */ }
  }

  on_selection_changed() {
    if (!this.gridApi) return;
    const picked = this.gridApi.getSelectedRows() as BreakoutRow[];
    this.selected = new Set(picked.map(r => this.row_key(r)));
    if (this.data) this.tsv = this.build_tsv(this.data);
  }

  // IT-F2-157 Mike c/97f291da (2026-10-30) — multi-month aggregate
  // mode. When ?months=M1,M2,...,M6 is in the URL, the backend widens
  // the window; the page header shows the range instead of a single
  // month label.
  months: string[] = [];

  ngOnInit() {
    this.route.queryParamMap.subscribe(params => {
      this.customer = params.get('customer') || '';
      this.scanner = params.get('scanner') || '';
      this.month = params.get('month') || '';
      this.billable_only = params.get('billable_only') === 'true';
      const months_raw = params.get('months') || '';
      this.months = months_raw.split(',').map(s => s.trim()).filter(s => /^\d{4}-\d{2}$/.test(s));
      this.load();
    });
  }

  async load() {
    this.loading = true;
    this.error = '';
    this.data = null;
    this.tsv = '';
    // IT-F2-157 Mike c/bb627ce0 (2026-10-30): range mode (?months=M1,...)
    // is valid without ?month=; need either single month or months array.
    if (!this.customer || !this.scanner || (!this.month && !this.months.length)) {
      this.error = 'Missing customer / scanner / month(s) query params.';
      this.loading = false;
      return;
    }
    const url = `${env.apiUrl}/rest/billing/breakout`
      + `?customer=${encodeURIComponent(this.customer)}`
      + `&scanner=${encodeURIComponent(this.scanner)}`
      + `&month=${encodeURIComponent(this.month)}`
      + (this.billable_only ? `&billable_only=true` : '')
      + (this.months?.length ? `&months=${encodeURIComponent(this.months.join(','))}` : '');
    try {
      const res: any = await this.http.get(url, { withCredentials: true }).toPromise();
      if (res?.err) throw new Error(res.err);
      this.data = res;
      this.tsv = this.build_tsv(res);
    } catch (e: any) {
      this.error = e?.message || String(e);
    } finally {
      this.loading = false;
    }
  }

  build_tsv(data: BreakoutResponse): string {
    const is_logins = data.source_kind === 'logins';
    const header = is_logins
      ? ['Email', 'First', 'Last', 'Customer', 'Scanner', 'First Login', 'Last Login', 'Source', 'Pool', 'Logins', 'Delta']
      : ['Email', 'First', 'Last', 'Customer', 'Scanner', 'Start', 'End', 'Source', 'Pool', 'Delta'];
    const lines = [header.join('\t')];
    // IT-F2-157 c/21eaaf56 (Mike 2026-10-01): respect per-row selection
    // so operators can scope the TSV to the users they picked. Zero
    // selection = export every row (prior behavior).
    const rows_to_use = this.selected.size > 0
      ? data.rows.filter(r => this.selected.has(this.row_key(r)))
      : data.rows;
    for (const r of rows_to_use) {
      // For login-row breakouts, Start / End are real UTC timestamps —
      // show them with time of day; for the UserTracking paths they
      // continue to render as calendar dates via format_date.
      const start_cell = is_logins ? this.format_datetime(r.start_date) : this.format_date(r.start_date);
      const end_cell   = is_logins ? this.format_datetime(r.end_date)   : this.format_date(r.end_date);
      const base = [
        r.email, r.first_name, r.last_name, r.customer, r.scanner,
        start_cell, end_cell,
        r.source, r.pool_id,
      ];
      const cells = is_logins
        ? [...base, String(r.login_count ?? ''), r.delta_type]
        : [...base, r.delta_type];
      lines.push(cells.map(v => String(v ?? '').replace(/\t/g, ' ')).join('\t'));
    }
    return lines.join('\n');
  }

  // IT-F2-157 c/21eaaf56 (Mike 2026-10-01): per-row key for selection
  // tracking; matches track_row so Angular's trackBy and our Set share
  // the same identity.
  row_key(r: BreakoutRow): string {
    return `${r.email}::${r.scanner}`;
  }

  toggle_row(r: BreakoutRow, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    const k = this.row_key(r);
    if (checked) this.selected.add(k); else this.selected.delete(k);
    this.tsv = this.data ? this.build_tsv(this.data) : '';
  }

  is_selected(r: BreakoutRow): boolean {
    return this.selected.has(this.row_key(r));
  }

  toggle_all(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    if (!this.data) return;
    this.selected = new Set(checked ? this.data.rows.map(r => this.row_key(r)) : []);
    this.tsv = this.build_tsv(this.data);
  }

  all_selected(): boolean {
    return !!this.data && this.data.rows.length > 0 && this.selected.size === this.data.rows.length;
  }

  // IT-F2-157 Mike c/7d710eb1 (2026-10-30): the Copy to Excel button
  // label advertises exactly how many rows will land on the clipboard.
  // Selected size wins when any row is picked; otherwise the grid's
  // displayed-row count (respects active filters per Mike c/b41aa4b6
  // merging billable + non-billable into one view).
  copy_count(): number {
    if (this.selected.size > 0) return this.selected.size;
    if (this.gridApi) {
      try { return this.gridApi.getDisplayedRowCount(); } catch { /* fallthrough */ }
    }
    const bill = this.data?.rows?.length || 0;
    const non = this.data?.non_billable_rows?.length || 0;
    return bill + non;
  }

  // IT-F2-157 Mike c/edca24cd (2026-10-30): "Back to Billing Report"
  // must preserve the customer + Billable/full-report scope the user
  // drilled from, otherwise the back-nav lands on the fleet-wide report
  // in the opened tab and the admin loses context.
  back_query_params(): Record<string, string> {
    const q: Record<string, string> = {};
    if (this.customer) q['customer'] = this.customer;
    if (this.billable_only) q['billable_only'] = 'true';
    return q;
  }

  // IT-F2-157 Mike c/144ca889 + operator green-light (2026-10-30) —
  // Retro-revoke preview modal state. Read-only — opens the preview
  // endpoint payload for the current scanner so Mike can validate the
  // specific cohort before any write-enabled Phase 2 ships. Writes
  // stay blocked pending explicit second operator sign-off AFTER
  // review of this preview.
  retro_preview_open = false;
  retro_preview_loading = false;
  retro_preview_err = '';
  retro_preview: any = null;

  async open_retro_revoke_preview() {
    if (!this.scanner) return;
    this.retro_preview_open = true;
    this.retro_preview_loading = true;
    this.retro_preview_err = '';
    this.retro_preview = null;
    const q = new URLSearchParams({ scanner: this.scanner });
    if (this.customer) q.set('customer', this.customer);
    const url = `${env.apiUrl}/rest/billing/retro-revoke-preview?${q.toString()}`;
    try {
      const res: any = await this.http.get(url, { withCredentials: true }).toPromise();
      if (res?.err) throw new Error(res.err);
      this.retro_preview = res;
    } catch (e: any) {
      this.retro_preview_err = e?.message || String(e);
    } finally {
      this.retro_preview_loading = false;
    }
  }

  async copy_retro_preview() {
    if (!this.retro_preview) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(this.retro_preview, null, 2));
    } catch { /* ignore */ }
  }

  export_retro_preview_csv() {
    if (!this.retro_preview?.plans) return;
    const header = ['email','customer','scanner','pool_id','enabled','current_scanners','cognito_after'];
    const lines = [header.join(',')];
    for (const p of this.retro_preview.plans) {
      const row = [
        p.email || '',
        p.customer || '',
        p.scanner || '',
        p.pool_id || '',
        p.enabled === null ? 'not_found' : (p.enabled ? 'yes' : 'no'),
        (p.current_scanners || []).join('|'),
        p.plan?.cognito?.after || '',
      ].map(v => {
        const s = String(v ?? '');
        return (s.includes(',') || s.includes('"')) ? '"' + s.replace(/"/g, '""') + '"' : s;
      });
      lines.push(row.join(','));
    }
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `retro-revoke-preview-${this.scanner}-${this.month}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // IT-F2-157 c/21eaaf56 (Mike 2026-10-01): display real datetime for
  // login-row Start / Last timestamps. ET-formatted for operator
  // consumption, matching the compliance-report convention.
  format_datetime(iso: string | null): string {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return String(iso);
      // yyyy-MM-dd HH:mm in local ET (simple pass — month-level
      // bucketing is already ET on the aggregation side; this is just
      // a readable render).
      return d.toLocaleString('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
    } catch { return String(iso); }
  }

  // IT-F2-157 c/9a55ad31 (Mike 2026-10-01): Copy Debug on breakout page.
  // Payload mirrors the billing-reports Copy Debug shape — raw backend
  // response + the query that drove it, so a 0-row result is diagnosable
  // without needing prod Mongo access.
  async copy_debug() {
    // IT-F2-157 c/21eaaf56 (Mike 2026-10-01): scope payload to selected
    // rows when any are picked. "Include raw data" toggle controls
    // whether the whole backend response is embedded or only the
    // selected rows + counts.
    const selected_rows = this.data
      ? (this.selected.size > 0
          ? this.data.rows.filter(r => this.selected.has(this.row_key(r)))
          : this.data.rows)
      : [];
    const payload_response = this.data ? {
      ...this.data,
      rows: this.debug_include_data ? selected_rows : [],
    } : null;
    const snapshot = {
      timestamp: new Date().toISOString(),
      label: 'Billing Breakout — Copy Debug',
      url: window.location.href,
      query: { customer: this.customer, scanner: this.scanner, month: this.month },
      error: this.error || null,
      counts: {
        rows_total: this.data?.rows?.length ?? 0,
        rows_selected: this.selected.size,
        rows_in_payload: this.debug_include_data ? selected_rows.length : 0,
        count: this.data?.count ?? 0,
        joined: this.data?.joined_count ?? 0,
        departed: this.data?.departed_count ?? 0,
        total_logins: this.data?.total_logins ?? null,
      },
      source_kind: this.data?.source_kind ?? null,
      include_data: this.debug_include_data,
      backend_response: payload_response,
    };
    this.debug_state = 'idle';
    try {
      await navigator.clipboard.writeText(JSON.stringify(snapshot, null, 2));
      this.debug_state = 'ok';
    } catch {
      this.debug_state = 'err';
    } finally {
      setTimeout(() => { this.debug_state = 'idle'; }, 2500);
    }
  }

  async copy_to_excel() {
    this.copying = true;
    this.copy_state = 'idle';
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(this.tsv);
        this.copy_state = 'ok';
      } else {
        // Fallback via hidden textarea + execCommand — required in
        // Chrome iframe contexts (feedback_er_codes_always_have_copy_button).
        const ta = document.createElement('textarea');
        ta.value = this.tsv;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        this.copy_state = ok ? 'ok' : 'err';
      }
    } catch {
      this.copy_state = 'err';
    } finally {
      this.copying = false;
      setTimeout(() => { this.copy_state = 'idle'; }, 2500);
    }
  }

  format_date(iso: string | null): string {
    if (!iso) return '';
    try { return new Date(iso).toISOString().slice(0, 10); } catch { return String(iso); }
  }

  month_label(m: string): string {
    if (!/^\d{4}-\d{2}$/.test(m)) return m;
    const [y, mo] = m.split('-').map(Number);
    return new Date(y, mo - 1, 1).toLocaleString('en-US', { month: 'short', year: 'numeric' });
  }

  track_row(_: number, r: BreakoutRow): string {
    return `${r.email}::${r.scanner}`;
  }
}
